import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy, XCircle, Pause, CheckCircle2, Users } from "lucide-react";
import { fmtDate, STAGES as PIPELINE_STAGES, TRAINING_STAGES } from "@/lib/format";

// Canonical pipeline orders — sourced from /lib/format so the timeline
// stays in lock-step with the kanban and the backend stage lists. Terminal
// triplet (won / lost / dropped) is shared by every pipeline. We declare
// our own row map here so we can attach the `terminal` flag the renderer
// uses to special-case the closing dot.
const TERMINAL_KEYS = new Set(["won", "lost", "dropped"]);

function buildStageRows(pipelineType) {
  // Pick the right stage list for this deal's pipeline. Anything that
  // isn't explicitly "training" falls back to the standard product/service
  // pipeline — matches `stagesForPipeline()` in /lib/format.
  const src = pipelineType === "training" ? TRAINING_STAGES : PIPELINE_STAGES;
  // The timeline uses friendlier short labels for the standard pipeline
  // (e.g. "Demo" instead of "Demonstration") to keep the dots compact on
  // mobile. For Training we use the full label since there are only 3.
  const shortLabels = {
    qualification: "Qualification",
    demonstration: "Demo",
    decision_makers: "Decision makers",
    technical_evaluation: "Tech eval.",
    budgetary_quotes: "Budgetary quote",
    under_budget_approval: "Under budget approval",
    budget_approval: "Budget approved",
    purchase_enquiry: "Purchase enquiry",
    negotiation: "Negotiation",
    won: "Won",
    lost: "Lost",
    dropped: "Dropped",
  };
  return src.map((s) => ({
    key: s.key,
    label: shortLabels[s.key] || s.label,
    terminal: TERMINAL_KEYS.has(s.key),
  }));
}

// For each visited stage, find the first segment that landed on it. We use
// the EARLIEST visit timestamp because that's when the deal "first reached"
// that stage — even if the rep bounced backward and forward, the milestone
// date is the original arrival.
function buildVisits(segments = []) {
  const visits = new Map();
  for (const s of segments) {
    if (!visits.has(s.stage)) visits.set(s.stage, s.entered_at);
  }
  return visits;
}

function totalDaysIn(stage, segments = []) {
  return segments
    .filter((s) => s.stage === stage)
    .reduce((acc, s) => acc + (s.days_in_stage || 0), 0);
}

export default function DealTimeline({ dealId, pipelineType = "product", decisionMakers = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Per-pipeline stage list — refreshed whenever the parent route swaps in
  // a deal from a different pipeline.
  const STAGES = buildStageRows(pipelineType);

  useEffect(() => {
    let cancelled = false;
    if (!dealId) return;
    setLoading(true);
    api.get(`/deals/${dealId}/timeline`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dealId]);

  if (loading) {
    return (
      <Card className="rounded-3xl border-border/60 shadow-soft mb-6">
        <CardContent className="p-6"><Skeleton className="h-32 w-full rounded-2xl" /></CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const visits = buildVisits(data.segments);
  const current = data.current_stage;
  const currentIdx = STAGES.findIndex((s) => s.key === current);
  const isClosed = data.is_closed;

  // Decide which stages to render. If this deal hit a terminal stage we show
  // the path that was actually walked (open stages visited + the terminal).
  // For active deals we show the full forward path so the user sees what's
  // left. "Open" = any non-terminal stage in this pipeline.
  const renderable = STAGES.filter((s) => {
    if (s.terminal) return s.key === current;
    if (isClosed) return visits.has(s.key);  // only visited non-terminal stages
    return true; // open deal: show every non-terminal stage in this pipeline
  });

  return (
    <Card className="rounded-3xl border-border/60 shadow-soft mb-6" data-testid="deal-timeline-card">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Stage timeline</p>
            <p className="text-xs text-muted-foreground mt-0.5">Started {fmtDate(data.started_at)} · {data.total_days} day{data.total_days === 1 ? "" : "s"} {isClosed ? "to close" : "in the pipeline"}</p>
          </div>
          {current === "won" && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold border border-emerald-200">
              <Trophy className="h-3.5 w-3.5" /> Won in {data.total_days}d
            </span>
          )}
          {current === "lost" && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-700 text-xs font-bold border border-red-200">
              <XCircle className="h-3.5 w-3.5" /> Lost
            </span>
          )}
          {current === "dropped" && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-stone-100 text-stone-700 text-xs font-bold border border-stone-200">
              <Pause className="h-3.5 w-3.5" /> Dropped
            </span>
          )}
        </div>

        {/* Horizontal scroll on mobile so the dots never squish. */}
        <div className="overflow-x-auto -mx-2 px-2 pb-2">
          <ol className="flex items-stretch gap-0 min-w-max">
            {renderable.map((s, i) => {
              const visited = visits.has(s.key);
              const isCurrent = s.key === current;
              const date = visits.get(s.key);
              const days = totalDaysIn(s.key, data.segments);
              const isLast = i === renderable.length - 1;

              const dotTone = s.key === "won"
                ? "bg-emerald-500 text-white border-emerald-600 ring-2 ring-emerald-100"
                : s.key === "lost"
                ? "bg-red-500 text-white border-red-600 ring-2 ring-red-100"
                : s.key === "dropped"
                ? "bg-stone-400 text-white border-stone-500"
                : isCurrent
                ? "bg-primary text-primary-foreground border-primary ring-4 ring-primary/20"
                : visited
                ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                : "bg-muted text-muted-foreground border-border/60";

              const segmentTone = visited && !isCurrent ? "bg-emerald-300" : "bg-border/60";

              return (
                <li key={s.key} className="flex flex-col items-center min-w-[6.5rem]" data-testid={`timeline-stage-${s.key}`}>
                  {/* Dot + connector row */}
                  <div className="relative w-full flex items-center justify-center">
                    {i > 0 && (
                      <span
                        aria-hidden
                        className={`absolute left-0 right-1/2 top-1/2 h-0.5 -translate-y-1/2 ${segmentTone}`}
                      />
                    )}
                    {!isLast && (
                      <span
                        aria-hidden
                        className={`absolute left-1/2 right-0 top-1/2 h-0.5 -translate-y-1/2 ${
                          (visited && visits.has(renderable[i + 1]?.key)) ? "bg-emerald-300" : "bg-border/60"
                        }`}
                      />
                    )}
                    <span
                      className={`relative h-8 w-8 rounded-full border-2 grid place-items-center font-bold text-[11px] ${dotTone}`}
                      title={visited ? `Entered ${fmtDate(date)}` : "Not yet reached"}
                    >
                      {s.key === "won" ? <Trophy className="h-3.5 w-3.5" />
                        : s.key === "lost" ? <XCircle className="h-3.5 w-3.5" />
                        : s.key === "dropped" ? <Pause className="h-3.5 w-3.5" />
                        : visited ? <CheckCircle2 className="h-3.5 w-3.5" />
                        : <span>{i + 1}</span>}
                    </span>
                  </div>
                  {/* Stage label + date + duration */}
                  <p className={`text-[10px] uppercase tracking-wider font-bold mt-2 text-center ${isCurrent ? "text-primary" : visited ? "text-foreground" : "text-muted-foreground"}`}>
                    {s.label}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5 text-center whitespace-nowrap">
                    {visited ? fmtDate(date) : "—"}
                  </p>
                  {visited && days > 0 && (
                    <p className="text-[9px] text-muted-foreground mt-0.5 text-center">
                      {days < 1 ? "<1d" : `${Math.round(days)}d`}
                    </p>
                  )}
                </li>
              );
            })}
          </ol>
        </div>

        {/* Decision makers — show across EVERY stage so reps see who they're
            talking to whatever the deal's current funnel position. Stays visible
            even after the deal closes. */}
        {decisionMakers && decisionMakers.length > 0 && (
          <div className="mt-5 pt-4 border-t border-border/40" data-testid="timeline-decision-makers">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Decision makers ({decisionMakers.length})</p>
            <div className="flex flex-wrap gap-2">
              {decisionMakers.map((dm, i) => (
                <span
                  key={dm.id || `${dm.name}-${i}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-2xl text-[11px] font-medium bg-blue-50 border border-blue-200 text-blue-900"
                  data-testid={`timeline-dm-${i}`}
                  title={[dm.name, dm.designation, dm.phone, dm.email].filter(Boolean).join(" · ")}
                >
                  <span className="h-5 w-5 grid place-items-center rounded-full bg-blue-200 text-blue-900 text-[10px] font-bold">{(dm.name || "?").slice(0, 1).toUpperCase()}</span>
                  <span className="font-semibold">{dm.name || "Unnamed"}</span>
                  {dm.designation && <span className="text-blue-700/70">· {dm.designation}</span>}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Bounce / revert audit trail — show every backward jump so reps
            can see where the deal stalled or slipped back. */}
        {(() => {
          const reverts = [];
          for (let i = 1; i < (data.segments || []).length; i += 1) {
            const prev = data.segments[i - 1];
            const curr = data.segments[i];
            const prevIdx = STAGES.findIndex((s) => s.key === prev.stage);
            const currIdx = STAGES.findIndex((s) => s.key === curr.stage);
            if (currIdx >= 0 && prevIdx >= 0 && currIdx < prevIdx) {
              reverts.push({ from: prev.stage, to: curr.stage, at: curr.entered_at });
            }
          }
          if (!reverts.length) return null;
          return (
            <div className="mt-5 pt-4 border-t border-border/40">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Reverts ({reverts.length})</p>
              <div className="space-y-1">
                {reverts.map((r, i) => (
                  <p key={`${r.at}-${r.from}-${r.to}-${i}`} className="text-xs text-amber-700">
                    {fmtDate(r.at)} — moved back from <strong className="capitalize">{r.from.replaceAll("_", " ")}</strong> to <strong className="capitalize">{r.to.replaceAll("_", " ")}</strong>
                  </p>
                ))}
              </div>
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
}
